import { lazy, Suspense, type ReactNode } from "react";
import { PinnedMessagesPanel } from "../PinnedMessagesPanel";
import { MemberListPanel } from "../MemberListPanel";
import { ThreadPanel } from "@/components/messages/ThreadPanel";
import { ControlTowerPanel } from "@/components/sync/ControlTowerPanel";
import { useIsMobile } from "@/hooks/use-is-mobile";
import { Sheet, SheetContent } from "@/components/ui/sheet";

/**
 * On mobile (`<md:`) each right-side panel renders inside a Sheet
 * drawer that slides in from the right and dims the rest of the UI.
 * On desktop (`md:` and above) the panel renders inline as a flex
 * sibling — byte-identical to today. The wrapper just swaps the parent
 * based on viewport.
 */
function MobilePanelSheet({
  open,
  onClose,
  children,
}: {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
}) {
  return (
    <Sheet
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <SheetContent
        side="right"
        showClose={false}
        className="p-0 w-[90vw] max-w-md flex flex-col"
      >
        {children}
      </SheetContent>
    </Sheet>
  );
}

const LazyInviteDialog = lazy(() =>
  import("@/components/dialogs/InviteDialog").then((m) => ({
    default: m.InviteDialog,
  })),
);

const LazyProfileDialog = lazy(() =>
  import("@/components/dialogs/ProfileDialog").then((m) => ({
    default: m.ProfileDialog,
  })),
);

type UserInfo = {
  display_name: string;
  avatar_url?: string | null;
  is_online?: boolean;
};

type PinMessageRow = {
  id: string;
  user_id: string;
  content: string;
  created_at: number;
  sender?: UserInfo | null;
};

interface MessageAreaPanelsProps {
  channelId: string;

  // Pinned panel
  showPinnedMessages: boolean;
  visibleChannelPins: readonly { id: string; message_id: string }[];
  messageById: ReadonlyMap<string, PinMessageRow>;
  renderUserMap: ReadonlyMap<string, UserInfo>;
  onClosePinned: () => void;
  onScrollToMessage: (messageId: string) => void;

  // Control Tower panel (Ano Sync)
  showControlTower: boolean;
  onCloseControlTower: () => void;

  // Member panel
  showMembers: boolean;
  onCloseMembers: () => void;
  channelMemberList: readonly { id: string; user_id: string }[];
  /**
   * `activeUserMap` carries `is_online: boolean | null | undefined`,
   * which is structurally incompatible with `UserInfo`'s `is_online:
   * boolean | undefined`. Typed as `unknown` here to match the
   * existing `as any` cast in `MemberListPanel`'s call site
   * (preserved verbatim from the pre-split parent).
   */
  activeUserMap: ReadonlyMap<string, unknown>;

  // Thread panel
  activeThread: string | null;
  workspaceMembers: { id: string; display_name: string }[];
  workspaceChannels: { id: string; name: string }[];
  activeWorkspaceMemberIds: Set<string> | null;
  onCloseThread: () => void;
  onUserClick: (userId: string) => void;
  onChannelClick: (channelId: string) => void;
  /** Undefined in browser — the Shell is Electron-only. */
  onSendToShell?: (
    messageId: string,
    selection?: string,
    msgOverride?: {
      id: string;
      content: string;
      user_id: string;
      thread_id?: string | null;
      created_at: number;
    },
  ) => void;
  isCoworkerDm: boolean;
  coworkerIds?: Set<string>;
  threadScrollTargetId: string | null;
  onThreadScrollHandled: () => void;
  hidden: boolean;

  // Lazy dialogs
  dmInviteContext:
    | import("@/components/dialogs/InviteDialog").InviteContext
    | null;
  onCloseDmInvite: () => void;
  profileUserId: string | null;
  onCloseProfile: () => void;
  /** Open a 1:1 DM with the viewed user (the profile card's "Message" action). */
  onStartDmFromProfile: (userId: string) => void;
}

/**
 * Render leaf for the right-side panels + lazy-loaded dialogs that
 * `MessageArea` mounts as siblings of the message column:
 *
 * - `PinnedMessagesPanel` — when `showPinnedMessages` is on AND there's
 *   at least one visible (non-orphan) pin.
 * - `MemberListPanel` — when `showMembers` is on. `userMap` cast to
 *   `any` upstream because `activeUserMap` carries `is_online` that
 *   the panel reads directly.
 * - `ThreadPanel` — when a thread is active for this channel.
 * - `InviteDialog` (lazy) — DM/group-DM invite-to-conversation flow.
 * - `ProfileDialog` (lazy) — opened from the header avatar menu.
 *
 * Pure render; all state lives in the parent. Extracted to keep the
 * `MessageArea` JSX focused on the message column itself.
 */
export function MessageAreaPanels({
  channelId,
  showPinnedMessages,
  visibleChannelPins,
  messageById,
  renderUserMap,
  onClosePinned,
  onScrollToMessage,
  showControlTower,
  onCloseControlTower,
  showMembers,
  onCloseMembers,
  channelMemberList,
  activeUserMap,
  activeThread,
  workspaceMembers,
  workspaceChannels,
  activeWorkspaceMemberIds,
  onCloseThread,
  onUserClick,
  onChannelClick,
  onSendToShell,
  isCoworkerDm,
  coworkerIds,
  threadScrollTargetId,
  onThreadScrollHandled,
  hidden,
  dmInviteContext,
  onCloseDmInvite,
  profileUserId,
  onCloseProfile,
  onStartDmFromProfile,
}: MessageAreaPanelsProps) {
  const isMobile = useIsMobile();

  const pinnedNode =
    showPinnedMessages && visibleChannelPins.length > 0 ? (
      <PinnedMessagesPanel
        pins={visibleChannelPins}
        messageById={messageById}
        userMap={renderUserMap}
        onClose={onClosePinned}
        onScrollToMessage={onScrollToMessage}
      />
    ) : null;

  const controlTowerNode = showControlTower ? (
    <ControlTowerPanel channelId={channelId} onClose={onCloseControlTower} />
  ) : null;

  const membersNode = showMembers ? (
    <MemberListPanel
      members={channelMemberList}
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      userMap={activeUserMap as any}
      onCoworkerClick={(id: string) => {
        window.dispatchEvent(
          new CustomEvent("ano:open-coworker", { detail: { id } }),
        );
      }}
    />
  ) : null;

  const threadNode = activeThread ? (
    <ThreadPanel
      parentMessageId={activeThread}
      channelId={channelId}
      workspaceMembers={workspaceMembers}
      channels={workspaceChannels}
      activeWorkspaceMemberIds={activeWorkspaceMemberIds}
      onClose={onCloseThread}
      onScrollToParent={() => onScrollToMessage(activeThread)}
      onUserClick={onUserClick}
      onChannelClick={onChannelClick}
      onSendToShell={onSendToShell}
      isCoworkerDm={isCoworkerDm}
      coworkerIds={coworkerIds}
      scrollToMessageId={threadScrollTargetId}
      onScrollHandled={onThreadScrollHandled}
      hidden={hidden}
    />
  ) : null;

  // Desktop: render inline as flex siblings (existing behavior).
  // Mobile: render each into its own Sheet drawer, max one open at a
  // time — at most one of the three panels is meant to be visible at
  // any moment anyway (their open-flag setters are mutually exclusive
  // in MessageArea).
  return (
    <>
      {isMobile ? (
        <>
          <MobilePanelSheet open={!!pinnedNode} onClose={onClosePinned}>
            {pinnedNode}
          </MobilePanelSheet>
          <MobilePanelSheet
            open={!!controlTowerNode}
            onClose={onCloseControlTower}
          >
            {controlTowerNode}
          </MobilePanelSheet>
          <MobilePanelSheet open={!!membersNode} onClose={onCloseMembers}>
            {membersNode}
          </MobilePanelSheet>
          <MobilePanelSheet open={!!threadNode} onClose={onCloseThread}>
            {threadNode}
          </MobilePanelSheet>
        </>
      ) : (
        <>
          {pinnedNode}
          {controlTowerNode}
          {membersNode}
          {threadNode}
        </>
      )}

      {dmInviteContext && (
        <Suspense fallback={null}>
          <LazyInviteDialog
            open={!!dmInviteContext}
            onOpenChange={(open) => {
              if (!open) onCloseDmInvite();
            }}
            context={dmInviteContext}
          />
        </Suspense>
      )}

      {profileUserId && (
        <Suspense fallback={null}>
          <LazyProfileDialog
            open={profileUserId != null}
            onOpenChange={(open) => {
              if (!open) onCloseProfile();
            }}
            userId={profileUserId}
            onStartDm={onStartDmFromProfile}
          />
        </Suspense>
      )}
    </>
  );
}
